import React from "react"

// components
import { Row, Col, Typography, Button } from "antd"

// firebase
import { onTokenChanged } from "firebase/app-check"
import { AppCheck } from "services/firebase"
import { LoadingOutlined } from "@ant-design/icons"

// config
import config from "config"

const AppCheckContext = React.createContext()

function useAppCheck() {
  const context = React.useContext(AppCheckContext)
  if (!context) {
    throw new Error(`useAppCheck must be used within a AppCheckProvider`)
  }

  const { tokenIsValid, errorValue } = context

  const [appCheckTokenIsValid] = tokenIsValid
  const [appCheckError] = errorValue

  return {
    hasValidAppCheck: appCheckTokenIsValid,
    appCheckError,
  }
}

function AppCheckProvider(props) {
  const [appCheckReady, setAppCheckReady] = React.useState(
    process.env.NEXT_PUBLIC__APP_ENV !== "production" ? true : false
  )
  const [appCheckError, setAppCheckError] = React.useState(null)
  const [appCheckTokenIsValid, setAppCheckTokenIsValid] = React.useState(false)
  const [showSkipButton, setShowSkipButton] = React.useState(false)
  const [appCheckSkipped, setAppCheckSkipped] = React.useState(false)

  React.useEffect(() => {
    setTimeout(() => {
      setShowSkipButton(true)
    }, 5 * 1000) // Show skip button after 5 seconds if not ready

    const unsubscribe = onTokenChanged(
      AppCheck,
      (token) => {
        setAppCheckReady(true)
        if (token) {
          setAppCheckTokenIsValid(true)
          setAppCheckError(null)
          console.log("✅ Verification token")
        } else {
          const error = new Error("No token available")
          console.error(error.message)
          console.log("⚠️ Verification token missing")

          setAppCheckTokenIsValid(false)
          setAppCheckError(error)
        }
      },
      (err) => {
        console.error(err.message)
        console.log("⚠️ Verification token failed")

        setAppCheckReady(true)
        setAppCheckTokenIsValid(false)
        setAppCheckError(err)
      }
    )
    return () => unsubscribe()
  }, [])

  const errorValue = React.useMemo(
    () => [appCheckError, setAppCheckError],
    [appCheckError]
  )
  const tokenIsValid = React.useMemo(
    () => [appCheckTokenIsValid, setAppCheckTokenIsValid],
    [appCheckTokenIsValid]
  )

  if (!appCheckReady) {
    return (
      <Row
        justify="center"
        align="middle"
        style={{ height: "100vh", width: "100%", padding: "0 20vw" }}
      >
        <Col>
          <Typography.Title
            level={5}
            style={{ textAlign: "center", marginBottom: 0 }}
          >
            {config.title}
          </Typography.Title>
          <Typography.Paragraph style={{ textAlign: "center", marginTop: 0 }}>
            {config.subtitle}
          </Typography.Paragraph>

          <LoadingOutlined
            style={{ fontSize: 24, margin: "auto", display: "block" }}
          />

          <Typography.Title
            level={5}
            style={{ textAlign: "center", fontStyle: "italic" }}
          >
            🔒 Sicherheitsüberprüfung
          </Typography.Title>

          <Typography.Paragraph style={{ textAlign: "center" }}>
            Sicherheitsüberprüfung läuft. Bitte warten Sie einen Moment, während
            wir Ihre Anfrage verifizieren.
            {showSkipButton && (
              <>
                <br />
                <Button
                  type="primary"
                  onClick={() => {
                    setAppCheckReady(true)
                    setAppCheckSkipped(true)
                    setAppCheckError(new Error("app-check-token-not-responded"))
                  }}
                  style={{ marginTop: 10 }}
                >
                  Weiter zum Restaurant
                </Button>
              </>
            )}
          </Typography.Paragraph>
        </Col>
      </Row>
    )
  }

  if (appCheckError && !appCheckSkipped) {
    return (
      <Row
        justify="center"
        align="middle"
        style={{ height: "100vh", width: "100%", padding: "0 15vw" }}
      >
        <Col>
          <Typography.Title level={5} style={{ textAlign: "center" }}>
            ⚠️ Achtung
          </Typography.Title>

          <Typography.Paragraph style={{ textAlign: "center", marginTop: 0 }}>
            Verbindung konnte nicht überprüft werden. Bitte benutzen Sie die
            aktuelle Version Ihres Browsers, laden Sie die Seite neu oder öffnen
            Sie unsere Seite <i>{config.url}</i> in einen separaten Browser.
          </Typography.Paragraph>

          <Typography.Paragraph style={{ textAlign: "center", color: "red" }}>
            Clicken Sie hier um fortzufahren.
            <br />
            <Button
              type="primary"
              onClick={() => {
                setAppCheckSkipped(true)
              }}
              style={{ marginTop: 10 }}
            >
              Weiter zum Restaurant
            </Button>
          </Typography.Paragraph>
        </Col>
      </Row>
    )
  }

  return (
    <AppCheckContext.Provider value={{ errorValue, tokenIsValid }} {...props} />
  )
}

export { AppCheckProvider, useAppCheck }
